import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import Link from "next/link";
import CloseButton from "../close_button";
import { useRouter } from "next/router";
import GetTranslatedStrings from "@helpers/GetTranslatedStrings";
import IcelandFlag from "@components/icons/flags/IcelandFlag";
import UKFlag from "@components/icons/flags/UKFlag";
import useUser from "@hooks/use_user";
import MenuBuilder from "@config/sidebars/innra_routes/MenuBuilder";
import { SideBarElement } from "@components/side_bar";
import NavigatableMenuContainer from "@components/side_bar/NavigatableMenuContainer/NavigatableMenuContainer";
import styles from "./SideMenu.module.scss";
import { AnimatePresence } from "framer-motion";
import { useTheme } from "@hooks/useTheme";
import { Sun, Moon, Monitor } from "lucide-react";

// Route class based on the pattern seen in MenuBuilder
class Route {
    constructor({ title, title_en, link, icon = null }) {
        this.title = title;
        this.title_en = title_en;
        this.link = link;
        this.icon = icon;
    }
}

function SideMenu({ show, onClose }) {
    const user = useUser();
    const t = GetTranslatedStrings();
    const router = useRouter();
    const [isMobile, setIsMobile] = useState(false);
    const [mounted, setMounted] = useState(false);
    const { theme, setThemeDirectly } = useTheme();

    // Memoize menu building to prevent rebuilding on every render
    const menu = useMemo(() => MenuBuilder(user), [user]);

    // Set mounted state when component mounts on client
    useEffect(() => {
        setMounted(true);
    }, []);

    // Create standard menu items
    const standardMenuItems = useMemo(
        () => [
            new Route({
                title: t.home,
                title_en: "Home",
                link: "/",
            }),
            new Route({
                title: t.map,
                title_en: "Map",
                link: "/kort",
            }),
            new Route({
                title: t.services_1819,
                title_en: "Services",
                link: "/um/thjonusta/vorur",
            }),
            new Route({
                title: t.about_1819,
                title_en: "About 1819",
                link: "/um/fyrirtaekid/um",
            }),
        ],
        [t],
    );

    useEffect(() => {
        if (show) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    }, [show]);

    // Check if screen is mobile width
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        // Initial check
        checkIsMobile();

        // Add event listener
        window.addEventListener("resize", checkIsMobile);

        // Cleanup
        return () => {
            window.removeEventListener("resize", checkIsMobile);
        };
    }, []);

    const getLanguageName = (locale) => {
        return locale === "is" ? "Íslenska" : "English";
    };

    return (
        <>
            <AnimatePresence>
                <div
                    className={classNames(styles.pageBlocker, { [styles.block]: show })}
                    onClick={onClose}
                />
            </AnimatePresence>
            <aside className={classNames(styles.sideMenu, { [styles.showMenu]: !show })}>
                <header className={styles.sideMenuHeader}>
                    {isMobile && (
                        <div className={styles.closeButtonContainer}>
                            <CloseButton
                                onClick={onClose}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    position: "relative",
                                }}
                            />
                        </div>
                    )}
                </header>
                <div className={styles.userSection}>
                    <div className={styles.userInfo}>
                        <Link href={mounted && user ? "/mitt/notandi" : "/innskraning"}>
                            <a className={styles.userDetails} onClick={onClose}>
                                <img src="/img/sidebar/user.png" alt="User" />
                                <p>{mounted && user ? user.full_name : "Mitt 1819"}</p>
                            </a>
                        </Link>
                        {mounted && user ? (
                            <Link href="/logout">
                                <a className={styles.logoutButton} onClick={onClose}>
                                    {t.logout}
                                </a>
                            </Link>
                        ) : (
                            <Link href="/innskraning">
                                <a className={styles.logoutButton} onClick={onClose}>
                                    {t.login}
                                </a>
                            </Link>
                        )}
                    </div>
                </div>
                <NavigatableMenuContainer
                    menu={menu}
                    onItemClick={onClose}
                    listViewClassName={styles.navigatableListPadding}
                    header={
                        <>
                            <div className={styles.languageSelector}>
                                <div className={styles.languageFlagsContainer}>
                                    <Link href={mounted ? router.asPath : "/"} locale="is">
                                        <a
                                            className={classNames(styles.languageFlag, {
                                                [styles.active]: router.locale === "is",
                                            })}
                                            onClick={onClose}
                                        >
                                            <IcelandFlag style={{ height: 40, width: 40 }} />
                                        </a>
                                    </Link>
                                    <Link href={mounted ? router.asPath : "/"} locale="en">
                                        <a
                                            className={classNames(styles.languageFlag, {
                                                [styles.active]: router.locale === "en",
                                            })}
                                            onClick={onClose}
                                        >
                                            <UKFlag style={{ height: 40, width: 40 }} />
                                        </a>
                                    </Link>
                                </div>
                                <div className={styles.languageLabel}>
                                    {getLanguageName(router.locale)}
                                </div>
                            </div>

                            {mounted &&
                                user &&
                                user.roles?.some((role) => role.name === "Þjónustufulltrúi") && (
                                    <div className={styles.themeSelector}>
                                        <div className={styles.themeButtonsContainer}>
                                            <button
                                                className={classNames(styles.themeButton, {
                                                    [styles.active]: theme === "light",
                                                })}
                                                onClick={() => setThemeDirectly("light")}
                                                aria-label="Light mode"
                                            >
                                                <Sun size={24} />
                                            </button>
                                            <button
                                                className={classNames(styles.themeButton, {
                                                    [styles.active]: theme === "dark",
                                                })}
                                                onClick={() => setThemeDirectly("dark")}
                                                aria-label="Dark mode"
                                            >
                                                <Moon size={24} />
                                            </button>
                                            <button
                                                className={classNames(styles.themeButton, {
                                                    [styles.active]: theme === "system",
                                                })}
                                                onClick={() => setThemeDirectly("system")}
                                                aria-label="System theme"
                                            >
                                                <Monitor size={24} />
                                            </button>
                                        </div>
                                        <div className={styles.themeLabel}>
                                            {theme === "light"
                                                ? "Ljóst þema"
                                                : theme === "dark"
                                                  ? "Dökkt þema"
                                                  : "Kerfis þema"}
                                        </div>
                                    </div>
                                )}
                        </>
                    }
                >
                    {/* Standard menu items */}
                    <div className={styles.standardMenuContainer}>
                        <nav>
                            {standardMenuItems.map((menuItem) => (
                                <SideBarElement
                                    key={menuItem?.title + menuItem?.link}
                                    menuItem={menuItem}
                                    onClick={onClose}
                                />
                            ))}
                        </nav>
                    </div>

                    {/* Mobile menu items - Always show */}
                    {mounted && menu && (
                        <div className={styles.mobileMenuContainer}>
                            <nav>
                                {menu
                                    .filter((menuItem) => menuItem?.title !== "Minn aðgangur")
                                    .map((menuItem) => (
                                        <SideBarElement
                                            key={menuItem?.title + menuItem?.link}
                                            menuItem={menuItem}
                                            onClick={onClose}
                                        />
                                    ))}
                            </nav>
                        </div>
                    )}
                </NavigatableMenuContainer>
            </aside>
        </>
    );
}

export default SideMenu;
